import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  User,
  LogOut,
  ChevronDown,
  Settings,
  Home,
  Users,
  FileText,
  Download,
  Shield
} from 'lucide-react';

const ROLE_LABELS = {
  'gerencia': 'Gerencia',
  'admin': 'Administrador',
  'aliado': 'Aliado',
  'comercial': 'Comercial',
  'contabilidad': 'Contabilidad',
  'asesor': 'Asesor',
  'diseñador': 'Diseñador'
};

const ROLE_COLORS = {
  'gerencia': 'bg-purple-500/20 text-purple-400',
  'admin': 'bg-red-500/20 text-red-400',
  'aliado': 'bg-[#D4AF37]/20 text-[#D4AF37]',
  'comercial': 'bg-blue-500/20 text-blue-400',
  'contabilidad': 'bg-green-500/20 text-green-400',
  'asesor': 'bg-cyan-500/20 text-cyan-400',
  'diseñador': 'bg-pink-500/20 text-pink-400'
};

export const UserMenu = ({ variant = 'default' }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cerrar menú al cambiar de ruta
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  if (!user) return null;

  const roleLabel = ROLE_LABELS[user.role] || user.role;
  const roleColor = ROLE_COLORS[user.role] || 'bg-zinc-500/20 text-zinc-400';
  
  // Obtener nombre a mostrar
  const displayName = user.username || user.email?.split('@')[0] || 'Usuario';

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  const getHomeRoute = () => {
    if (user.role === 'aliado') return '/panel-aliado';
    return '/dashboard';
  };

  const menuItems = [
    { 
      label: 'Mi Panel', 
      icon: Home, 
      onClick: () => navigate(getHomeRoute()),
      show: true
    },
    { 
      label: 'Mi Perfil', 
      icon: User, 
      onClick: () => navigate('/profile'),
      show: true
    },
    { 
      label: 'Configuración', 
      icon: Settings, 
      onClick: () => navigate('/admin/brand'),
      show: ['gerencia', 'admin'].includes(user.role)
    },
    { 
      label: 'Usuarios', 
      icon: Users, 
      onClick: () => navigate('/admin/users'),
      show: ['gerencia', 'admin'].includes(user.role)
    },
    { 
      label: 'Descargas', 
      icon: Download, 
      onClick: () => navigate('/descargas'),
      show: ['gerencia', 'admin', 'contabilidad'].includes(user.role)
    },
  ].filter(item => item.show);

  const isDark = variant === 'dark';

  return (
    <div className="relative" ref={menuRef}>
      {/* Botón del menú */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 sm:gap-3 px-1.5 sm:px-3 py-2 rounded-lg transition-colors ${
          isDark 
            ? 'hover:bg-zinc-800 text-white' 
            : 'hover:bg-zinc-100 text-zinc-900'
        }`}
        data-testid="user-menu-button"
      >
        {/* Avatar */}
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${roleColor}`}>
          <User className="w-5 h-5" />
        </div>
        
        {/* Nombre y rol - oculto en móvil */}
        <div className="hidden sm:block text-left">
          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-zinc-900'}`}>
            {displayName}
          </p>
          <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
            {roleLabel}
          </p>
        </div>
        
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''} ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`} />
      </button>

      {/* Menú desplegable */}
      {isOpen && (
        <div className={`absolute right-0 top-full mt-2 w-64 rounded-xl shadow-2xl border overflow-hidden z-50 ${
          isDark 
            ? 'bg-zinc-900 border-zinc-700' 
            : 'bg-white border-zinc-200'
        }`}>
          {/* Header del menú */}
          <div className={`px-4 py-3 border-b ${isDark ? 'border-zinc-700' : 'border-zinc-100'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${roleColor}`}>
                <User className="w-5 h-5" />
              </div>
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                  {displayName}
                </p>
                <div className="flex items-center gap-1.5">
                  <Shield className="w-3 h-3 text-[#D4AF37]" />
                  <span className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                    Rol: {roleLabel}
                  </span>
                </div>
              </div>
            </div>
            {user.email && (
              <p className={`text-xs mt-2 truncate ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                {user.email}
              </p>
            )}
          </div>

          {/* Opciones del menú */}
          <div className="py-2">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={item.onClick}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isDark 
                    ? 'text-zinc-300 hover:bg-zinc-800 hover:text-white' 
                    : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </div>

          {/* Botón de cerrar sesión */}
          <div className={`border-t ${isDark ? 'border-zinc-700' : 'border-zinc-100'}`}>
            <button
              onClick={handleLogout}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                isDark
                  ? 'text-red-400 hover:bg-red-500/10'
                  : 'text-red-600 hover:bg-red-50'
              }`}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente de barra de navegación completa
export const TopNavBar = ({ title, backHref, variant = 'dark', children }) => {
  const navigate = useNavigate();
  const isDark = variant === 'dark';

  return (
    <header className={`sticky top-0 z-40 ${isDark ? 'bg-zinc-950/80' : 'bg-white/80'} backdrop-blur-lg border-b ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Lado izquierdo */}
          <div className="flex items-center gap-4">
            {backHref && (
              <button
                onClick={() => navigate(backHref)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-zinc-100 text-zinc-600'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            )}
            {title && (
              <h1 className={`font-display text-lg ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                {title}
              </h1>
            )}
            {children}
          </div>

          {/* Lado derecho - Menú de usuario */}
          <UserMenu variant={variant} />
        </div>
      </div>
    </header>
  );
};

export default UserMenu;
